// Curatic cart helpers, loaded on every page (layout/theme.liquid).
// - addLines(): /cart/add.js + a standard CartLinesUpdateEvent on document, so Horizon's cart
//   drawer and cart bubble refresh themselves (an event from outside the drawer = external update).
// - refreshCart(): same event with no lines, after changing cart attributes.
// - openCart(): opens Horizon's cart drawer (#cart-drawer).
// - After any add, the drawer opens, unless the add was made quietly (event.curClaimed).
// - "Adaugă" buttons in the drawer upsell: [data-cur-add="<variant id>"].
import { CartLinesUpdateEvent } from '@shopify/events';

const fire = (action, lines, work, quiet = false) => {
  const deferred = CartLinesUpdateEvent.createPromise();
  const event = new CartLinesUpdateEvent({ action, context: 'product', lines, promise: deferred.promise });
  event.curOwn = true;
  event.curClaimed = quiet; // quiet: don't pop the cart open (adds made inside the checkout pop-up)
  document.dispatchEvent(event);
  return work()
    .then(async (res) => {
      const cart = await (await fetch('/cart.js')).json();
      deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { items: cart.items, source: 'cur-cart', didError: !!res?.status } });
      return cart;
    })
    .catch((e) => { deferred.reject(e); throw e; });
};

const post = (url, body) => fetch(url, {
  method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body),
}).then((r) => r.json());

export const addLines = (items, { quiet = false } = {}) =>
  fire('add', items.map((i) => ({ merchandiseId: String(i.id), quantity: i.quantity || 1 })), () => post('/cart/add.js', { items }), quiet);

export const refreshCart = () => fire('update', [], () => Promise.resolve({}));

export const openCart = () => {
  const d = document.getElementById('cart-drawer');
  if (d && !d.isOpen) d.open?.();
};

// Open the drawer after every add.
document.addEventListener('shopify:cart:lines-update', (e) => {
  if (e.action !== 'add' || e.curClaimed) return;
  Promise.resolve(e.promise).then((r) => { if (!r?.detail?.didError) openCart(); }).catch(() => {});
});

window.curCart = { addLines, refreshCart, openCart };

document.addEventListener('click', async (e) => {
  const b = e.target.closest('[data-cur-add]');
  if (!b || b.disabled) return;
  b.disabled = true;
  b.textContent = '…';
  try { await addLines([{ id: Number(b.dataset.curAdd), quantity: 1 }]); }
  catch { b.disabled = false; b.textContent = 'Adaugă'; }
});

// "Vreau una" buttons lower on the page ([data-cur-buy="<variant id>"] data-product="<product id>"): on that
// product's own page they press the real add-to-cart button, so the flow is identical;
// anywhere else they add the variant and open the cart. The href stays as the no-JS fallback.
document.addEventListener('click', async (e) => {
  const b = e.target.closest('[data-cur-buy]');
  if (!b) return;
  e.preventDefault();
  const main = [...document.querySelectorAll('form[data-type="add-to-cart-form"]')]
    .find((f) => f.querySelector(`input[name="product-id"][value="${b.dataset.product}"]`))
    ?.querySelector('button[name="add"]');
  if (main && !main.disabled) { main.click(); return; }
  if (b.getAttribute('aria-busy') === 'true') return;
  b.setAttribute('aria-busy', 'true');
  try { await addLines([{ id: Number(b.dataset.curBuy), quantity: 1 }]); }
  catch { location.href = b.href; }
  finally { b.removeAttribute('aria-busy'); }
});

// Free gift: once the rest of the cart reaches the threshold, the gift product is added once
// (an automatic Shopify discount makes it 0 lei); below the threshold it is removed again.
const giftCfg = (() => { try { return JSON.parse(document.querySelector('[data-cur-gift-config]')?.textContent || '{}'); } catch { return {}; } })();
let giftBusy = false;
export const syncGift = async () => {
  if (!giftCfg.variant || giftBusy) return false;
  giftBusy = true;
  try {
    const cart = await (await fetch('/cart.js')).json();
    const gifts = cart.items.filter((i) => i.variant_id === giftCfg.variant);
    const base = cart.items.filter((i) => i.variant_id !== giftCfg.variant).reduce((s, i) => s + i.final_line_price, 0);
    const want = cart.items.length > gifts.length && base >= giftCfg.min ? 1 : 0;
    const have = gifts.reduce((s, i) => s + i.quantity, 0);
    if (want === have) return false;
    if (have === 0) await addLines([{ id: giftCfg.variant, quantity: 1 }], { quiet: true });
    else {
      await post('/cart/change.js', { id: gifts[0].key, quantity: want });
      await refreshCart();
    }
    return true;
  } finally { giftBusy = false; }
};
window.curCart.syncGift = syncGift;
document.addEventListener('shopify:cart:lines-update', (e) => {
  Promise.resolve(e.promise).then(() => setTimeout(syncGift, 50)).catch(() => {});
});
syncGift();
